/**
 * ProfessionalContactButtons
 * Action buttons for contacting the professional
 * WhatsApp contact requires authentication validation
 */

import { InstagramLogoIcon } from '@radix-ui/react-icons'
import { Button } from '@shared/components/ui/button'
import { useAuth } from '@shared/contexts'
import { apiClient } from '@shared/lib/api'
import { API_ENDPOINTS } from '@shared/lib/api/endpoints'
import { actionStorage } from '@shared/lib/storage'
import {
  parseFacebookUrl,
  parseGoogleMapsUrl,
  parseInstagramUrl,
} from '@shared/lib/utils'
import {
  analytics,
  buildWhatsappContactProps,
  createContactAttemptId,
  EVENTS,
  WHATSAPP_STAGE,
  WHATSAPP_VIA,
} from '@shared/lib/analytics'
import { logger } from '@shared/lib/utils/logger'
import type { Professional } from '@shared/types'
import { Facebook, MapPin, MessageCircle, Share2 } from 'lucide-react'
import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

// ============================================================================
// Types
// ============================================================================

interface ProfessionalContactButtonsProps {
  professional: Professional
}

// ============================================================================
// Component
// ============================================================================

export function ProfessionalContactButtons({ professional }: ProfessionalContactButtonsProps) {
  const [copied, setCopied] = useState(false)
  const [isFetchingPhone, setIsFetchingPhone] = useState(false)
  const { user, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const handleWhatsAppClick = async () => {
    const contactAttemptId = createContactAttemptId(professional.id)
    const whatsappProps = buildWhatsappContactProps({
      professional_id: professional.id,
      professional_name: professional.professionalName,
      profession_name: professional.profession?.name,
      contact_attempt_id: contactAttemptId,
    }, WHATSAPP_VIA.DIRECT)

    if (!isAuthenticated) {
      analytics.capture(EVENTS.WHATSAPP_CONTACT, { ...whatsappProps, stage: WHATSAPP_STAGE.UNAUTHENTICATED })
      actionStorage.setPendingAction({
        type: 'contact_professional',
        data: {
          professionalId: professional.id,
          professionalName: professional.professionalName,
          professionName: professional.profession?.name,
          contactAttemptId,
        },
        timestamp: Date.now(),
        returnPath: location.pathname,
        message: '¡Abriendo chat de WhatsApp!',
      })
      toast.warning('Iniciá sesión para contactar profesionales.')
      await navigate('/login', { replace: true, state: { from: location } })
      return
    }

    if (!user?.isAccountValidated) {
      analytics.capture(EVENTS.WHATSAPP_CONTACT, { ...whatsappProps, stage: WHATSAPP_STAGE.UNVALIDATED })
      actionStorage.setPendingAction({
        type: 'contact_professional',
        data: {
          professionalId: professional.id,
          professionalName: professional.professionalName,
          professionName: professional.profession?.name,
          contactAttemptId,
        },
        timestamp: Date.now(),
        returnPath: location.pathname,
        message: '¡Ahora podés contactar al profesional por WhatsApp!',
      })
      toast.warning('Tenés que validar tu cuenta para contactar profesionales.')
      await navigate('/profile', { replace: true, state: { needsValidation: true } })
      return
    }

    setIsFetchingPhone(true)
    let contact: string | undefined
    try {
      const response = await apiClient.get<{ contact: string }>(
        API_ENDPOINTS.professionals.contactNumber(professional.id)
      )
      contact = response.contact
    } catch (err) {
      analytics.capture(EVENTS.WHATSAPP_CONTACT, { ...whatsappProps, stage: WHATSAPP_STAGE.FAILED, reason: 'api_error' })
      toast.error('No pudimos abrir WhatsApp. Probá de nuevo.')
      logger.error('contactNumber fetch failed', err)
      return
    } finally {
      setIsFetchingPhone(false)
    }

    if (typeof contact !== 'string' || !contact.trim()) {
      analytics.capture(EVENTS.WHATSAPP_CONTACT, { ...whatsappProps, stage: WHATSAPP_STAGE.FAILED, reason: 'no_contact' })
      toast.error('No pudimos obtener el número del profesional.')
      return
    }

    analytics.capture(EVENTS.PROFESSIONAL_CONTACT_DISCLOSED, whatsappProps)
    analytics.capture(EVENTS.WHATSAPP_CONTACT, {
      ...whatsappProps,
      stage: WHATSAPP_STAGE.ATTEMPTED,
    })
    toast.success('Abriendo WhatsApp...')
    const phone = contact.replace(/\D/g, '')
    const message = encodeURIComponent(
      `Hola ${professional.professionalName}, vi tu perfil en ProFessionals y me gustaría contactarte.`
    )
    try {
      window.open(`https://wa.me/${phone}?text=${message}`, '_blank')
    } catch (err) {
      toast.error('No pudimos abrir WhatsApp. Probá de nuevo.')
      logger.error('WhatsApp navigation failed', err)
    }
  }

  const handleShareClick = async () => {
    const url = window.location.href
    const text = `${professional.professionalName} - ${professional.profession?.name || 'Profesional'}`

    // Use native share API if available
    if (navigator.share) {
      try {
        await navigator.share({
          title: text,
          text: `Mira este profesional en ProFessionals: ${text}`,
          url,
        })
        // Success - no need for copied state
      } catch (err) {
        // User cancelled or error - do nothing
        if (err instanceof Error && err.name !== 'AbortError') {
          logger.error('Error sharing:', err)
        }
      }
    } else {
      // Fallback: copy to clipboard (only if no share API)
      try {
        await navigator.clipboard.writeText(url)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      } catch {
        toast.error('No se pudo compartir el enlace')
      }
    }
  }

  const handleInstagramClick = () => {
    if (!professional.instagramUrl) return

    const url = parseInstagramUrl(professional.instagramUrl)
    if (url && typeof url === 'string') {
      window.open(url, '_blank', 'noopener,noreferrer')
    } else {
      toast.error('URL de Instagram inválida')
    }
  }

  const handleFacebookClick = () => {
    if (!professional.facebookUrl) return

    const url = parseFacebookUrl(professional.facebookUrl)
    if (url && typeof url === 'string') {
      window.open(url, '_blank', 'noopener,noreferrer')
    } else {
      toast.error('URL de Facebook inválida')
    }
  }

  const handleMapsClick = () => {
    if (!professional.googleMapsUrl) return

    const url = parseGoogleMapsUrl(professional.googleMapsUrl)
    if (url && typeof url === 'string') {
      window.open(url, '_blank', 'noopener,noreferrer')
    } else {
      toast.error('Dirección de Google Maps inválida')
    }
  }

  return (
    <div className="py-4 sticky top-0 z-10">
      <div className="container flex flex-col-reverse flex-wrap mx-auto px-0 gap-2">
        {/* Primary Actions */}
        <div className="flex w-full">
          <Button
            size="lg"
            className="w-full"
            onClick={() => void handleWhatsAppClick()}
            disabled={isFetchingPhone}
          >
            <MessageCircle className="h-5 w-5 mr-2" />
            WhatsApp
          </Button>
        </div>

        <div className="flex flex-nowrap w-full gap-3">
          {/* Instagram */}
          <Button
            className="w-1/4"
            variant="ghost"
            onClick={handleInstagramClick}
            disabled={!professional.instagramUrl}
            title="Ver perfil de Instagram"
          >
            <InstagramLogoIcon className="h-5 w-5" />
          </Button>

          {/* Facebook */}
          <Button
            className="w-1/4"
            variant="ghost"
            onClick={handleFacebookClick}
            disabled={!professional.facebookUrl}
            title="Ver perfil de Facebook"
          >
            <Facebook className="h-5 w-5" />
          </Button>

          {/* Google Maps */}
          <Button
            className="w-1/4"
            variant="ghost"
            onClick={handleMapsClick}
            disabled={!professional.googleMapsUrl}
            title="Ver ubicación en Google Maps"
          >
            <MapPin className="h-5 w-5" />
          </Button>

          {/* Share */}
          <Button
            className="w-1/4"
            variant="ghost"
            onClick={() => void handleShareClick()}
            title="Compartir profesional"
          >
            <Share2 className="h-5 w-5" />
            {copied && <span className="ml-2 text-xs">Copiado!</span>}
          </Button>
        </div>
      </div>
    </div>
  )
}
